import React, { useState, useRef } from "react";
import {
  Box,
  ClickAwayListener,
  Paper,
  MenuItem,
  Button,
  Typography,
  Divider,
  useTheme,
} from "@mui/material";
import { AccountCircle, Login, PersonAdd } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { LOGIN_ROUTE_PATH, REGISTER_ROUTE_PATH } from "../../utils/constants/AppConstants";

const ProfileMenuComponent: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const menuRef = useRef<HTMLButtonElement>(null);

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const toLoginPage = () => {
    navigate(LOGIN_ROUTE_PATH);
    handleClose();
  };

  const toRegisterPage = () => {
    navigate(REGISTER_ROUTE_PATH);
    handleClose();
  };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box sx={{ position: "relative", display: "inline-block" }}>
        {/* Button */}
        <Button
          onClick={handleToggle}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          ref={menuRef}
          variant="outlined"
          startIcon={<AccountCircle />}
          sx={{
            borderRadius: 3,
            px: 3,
            py: 1,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: isHovered ? theme.palette.primary.light + '10' : theme.palette.background.paper,
            color: theme.palette.text.primary,
            fontWeight: 500,
            textTransform: 'none',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              border: `1px solid ${theme.palette.primary.main}`,
              backgroundColor: theme.palette.primary.light + '10',
              transform: 'translateY(-1px)',
              boxShadow: `0 4px 12px ${theme.palette.primary.main}20`,
            },
          }}
        >
          Get Started
        </Button>

        {/* Custom Dropdown */}
        {isOpen && (
          <Paper
            sx={{
              position: "absolute",
              top: "calc(100% + 8px)",
              right: 0,
              minWidth: "220px",
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              overflow: 'hidden',
              zIndex: 1000,
              background: theme.palette.background.paper,
            }}
          >
            <Box sx={{ p: 2, pb: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Welcome! Join us to start tracking
              </Typography>
            </Box>
            <Divider />
            <MenuItem 
              onClick={toRegisterPage}
              sx={{
                py: 1.5,
                px: 2,
                gap: 2,
                '&:hover': {
                  backgroundColor: theme.palette.success.light + '15',
                  color: theme.palette.success.dark,
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <PersonAdd sx={{ fontSize: '1.2rem' }} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Create Account
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  It's free and takes 1 minute
                </Typography>
              </Box>
            </MenuItem>
            <MenuItem 
              onClick={toLoginPage}
              sx={{
                py: 1.5,
                px: 2,
                gap: 2,
                '&:hover': {
                  backgroundColor: theme.palette.primary.light + '15',
                  color: theme.palette.primary.dark,
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <Login sx={{ fontSize: '1.2rem' }} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Sign In
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Access your dashboard
                </Typography>
              </Box>
            </MenuItem>
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default ProfileMenuComponent;
